/**
 * PaymentSelectionPanel — Reusable Phaser Container for chain + stablecoin selection.
 *
 * Extracted from ShipStoreDialog's toggle button pattern. Embeddable in any purchase
 * confirmation view that needs blockchain/stablecoin selection.
 *
 * Usage:
 *   const panel = new PaymentSelectionPanel(scene, 0, y, width);
 *   container.add(panel);
 *   // Later:
 *   panel.getSelectedChain();   // => 'solana'
 *   panel.getSelectedStablecoin(); // => 'USDC'
 */

import Phaser from 'phaser';
import type { Chain, Stablecoin } from '../../shared/common/MonetizationTypes';
import { Colors, Fonts, FontSize, Radius } from './GameUITheme';

const CHAINS: { id: Chain; label: string }[] = [
  { id: 'solana',    label: 'Solana' },
  { id: 'ethereum',  label: 'Ethereum' },
  { id: 'bsc',       label: 'BSC' },
  { id: 'galachain', label: 'GalaChain' },
];

const STABLECOINS: { id: Stablecoin; label: string }[] = [
  { id: 'USDC', label: 'USDC' },
  { id: 'USDT', label: 'USDT' },
];

export class PaymentSelectionPanel extends Phaser.GameObjects.Container {
  private selectedChain: Chain = 'solana';
  private selectedStablecoin: Stablecoin = 'USDC';
  private panelWidth: number;
  private onChanged?: () => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    options?: {
      defaultChain?: Chain;
      defaultStablecoin?: Stablecoin;
      onChanged?: () => void;
    },
  ) {
    super(scene, x, y);
    this.panelWidth = width;
    if (options?.defaultChain) this.selectedChain = options.defaultChain;
    if (options?.defaultStablecoin) this.selectedStablecoin = options.defaultStablecoin;
    this.onChanged = options?.onChanged;
    this.render();
    scene.add.existing(this);
  }

  getSelectedChain(): Chain { return this.selectedChain; }
  getSelectedStablecoin(): Stablecoin { return this.selectedStablecoin; }

  /** Total height consumed by this panel (for layout calculations). */
  getHeight(): number { return 86; }

  private render(): void {
    this.removeAll(true);
    let y = 0;

    // Chain label
    this.add(this.scene.add.text(0, y, 'BLOCKCHAIN', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    // Chain toggle buttons
    let cx = 0;
    const chainBtnW = Math.floor((this.panelWidth - (CHAINS.length - 1) * 6) / CHAINS.length);
    for (const chain of CHAINS) {
      const isActive = chain.id === this.selectedChain;
      this.add(this.createToggleBtn(cx, y, chainBtnW, 28, chain.label, isActive, () => {
        this.selectedChain = chain.id;
        this.render();
        this.onChanged?.();
      }));
      cx += chainBtnW + 6;
    }
    y += 38;

    // Stablecoin label
    this.add(this.scene.add.text(0, y, 'PAY WITH', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    // Stablecoin toggle buttons
    let sx = 0;
    const coinBtnW = Math.floor((this.panelWidth - (STABLECOINS.length - 1) * 6) / STABLECOINS.length);
    for (const coin of STABLECOINS) {
      const isActive = coin.id === this.selectedStablecoin;
      this.add(this.createToggleBtn(sx, y, coinBtnW, 28, coin.label, isActive, () => {
        this.selectedStablecoin = coin.id;
        this.render();
        this.onChanged?.();
      }));
      sx += coinBtnW + 6;
    }
  }

  private createToggleBtn(
    x: number, y: number, w: number, h: number,
    label: string, active: boolean, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (isActive: boolean, hover: boolean) => {
      bg.clear();
      if (isActive) {
        bg.fillStyle(Colors.cyan, 1);
        bg.fillRoundedRect(0, 0, w, h, Radius.sm);
        bg.lineStyle(1, 0x66ccff, 0.6);
        bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      } else {
        bg.fillStyle(hover ? 0x2a3a5a : 0x1e2844, 0.8);
        bg.fillRoundedRect(0, 0, w, h, Radius.sm);
        bg.lineStyle(1, hover ? 0x445577 : 0x334466, 0.4);
        bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      }
    };
    draw(active, false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs,
      color: active ? '#000000' : '#99aacc',
      fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => {
      if (!active) draw(false, true);
      this.scene.input.setDefaultCursor('pointer');
    });
    zone.on('pointerout', () => {
      draw(active, false);
      this.scene.input.setDefaultCursor('default');
    });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }
}
